import { createSelector } from '@reduxjs/toolkit';
import _ from 'lodash';
import questionFunctions from '../utils/functions/question';
import codes from '../utils/constants/codes';
import ids from '../utils/constants/ids';
import claimBankDetailsQuestionCodes from '../utils/constants/claimBankDetailsQuestionsCodes';
import GENERAL_FUNCTIONS from '../utils/functions/functions';

const claimApplicationProjections = {};

claimApplicationProjections.claimApplicationList = createSelector(
    state => state.claimApplication.claimApplication.byId,
    claimApplicationsById => {
        return Object.entries(claimApplicationsById).map(([key, value]) => ({
            ...value,
            formattedType: `${
                value.policyTypeName ? value.policyTypeName : ''
            } - 
                        ${
                            value.claimCategoryName
                                ? value.claimCategoryName
                                : ''
                        } 
                        ${
                            value.formTypeName ? ' - ' + value.formTypeName : ''
                        }`,
        }));
    },
);

claimApplicationProjections.claimApplication = claimApplicationId =>
    createSelector(
        state =>
            state.claimApplication.claimApplication.byId[claimApplicationId],
        claimApplication => {
            if (!claimApplication) {
                return null;
            }
            return {
                claimCategory: claimApplication.claimCategory,
                claimReference: claimApplication.claimReference,
                claimStatusName: claimApplication.claimStatusName,
                claimStateName: claimApplication.claimStateName,
                claimType: claimApplication.claimType,
                createdDate: claimApplication.createdDate,
                id: claimApplication.id,
                policyType: claimApplication.policyType,
                formType: claimApplication.formType,
                withdrawn: claimApplication.withdrawn
                    ? claimApplication.withdrawn
                    : false,
                formattedType: `${
                    claimApplication.policyType
                        ? claimApplication.policyType.name
                        : ''
                } - 
                        ${
                            claimApplication.claimCategory
                                ? claimApplication.claimCategory.name
                                : ''
                        }
                        ${
                            claimApplication.formType
                                ? ' - ' + claimApplication.formType.name
                                : ''
                        }`,
                claimantName:
                    claimApplication.claimant &&
                    claimApplication.claimant.fullName
                        ? claimApplication.claimant.fullName
                        : null,
                claimHandlerName:
                    claimApplication.assignee &&
                    claimApplication.assignee.fullName
                        ? claimApplication.assignee.fullName
                        : null,
                isBankDetailsVisible: claimApplication
                    ? claimApplication.isBankDetailsVisible
                    : false,
                claimApplicationApproval:
                    claimApplication.claimApplicationApproval
                        ? claimApplication.claimApplicationApproval
                        : null,
                isSurveyTabVisible: claimApplication
                    ? claimApplication.isSurveyTabVisible
                    : false,
                isAnonymised: claimApplication.isAnonymised,
            };
        },
    );

claimApplicationProjections.claimApplicationTemplate = claimApplicationId =>
    createSelector(
        state =>
            state.claimApplication.claimApplicationTemplate.byId[
                claimApplicationId
            ],
        claimApplicationTemplate => {
            if (!claimApplicationTemplate) {
                return null;
            }

            return {
                content: claimApplicationTemplate.content,
            };
        },
    );

claimApplicationProjections.claimApplicationAnswersGroupedByStep = (
    claimApplicationId,
    currencyOptions,
    groupId = undefined,
) =>
    createSelector(
        state =>
            state.claimApplication.claimApplication.byId[claimApplicationId],
        state => state.question.question.byId,
        state => state.questionGroupStep.questionGroupStep.byId,
        state => state.questionType.questionType.byId,
        state => state.question.question.allIds,
        (
            claimApplication,
            questionsById,
            questionGroupStepsById,
            questionTypesById,
            allQuestionIds,
        ) => {
            if (
                !claimApplication ||
                Object.keys(questionsById).length === 0 ||
                Object.keys(questionGroupStepsById).length === 0
            ) {
                return [];
            }

            let filteredQuestionsById = GENERAL_FUNCTIONS.filterObjectBy(
                questionsById,
                q => q.groupId === groupId,
            );
            // recursive function
            const getAnswersOfQuestionTree = (
                question,
                filteredQuestionsById,
                claimApplication,
                questionTypesById,
                currencyOptions,
            ) => {
                let items = [];
                const answerOfQuestion = claimApplication.answers.find(
                    a => a.questionCode === question.code,
                );
                const questionType = questionTypesById[question.questionTypeId];
                items.push(
                    questionFunctions.parseQuestionWithAnswer(
                        question,
                        answerOfQuestion,
                        questionType,
                        currencyOptions,
                    ),
                );

                // if option is selected form a dropdown then we have to get all
                //sub question answers
                if (
                    questionType &&
                    questionType.code === 'list' &&
                    answerOfQuestion
                ) {
                    const subQs = Object.values(filteredQuestionsById).filter(
                        q => q.enabledByOptionId === answerOfQuestion.optionId,
                    );
                    if (subQs) {
                        subQs.forEach(sq => {
                            items = [
                                ...items,
                                ...getAnswersOfQuestionTree(
                                    sq,
                                    filteredQuestionsById,
                                    claimApplication,
                                    questionTypesById,
                                    currencyOptions,
                                ),
                            ];
                        });
                    }
                }

                return items;
            };

            try {
                // construct the answers
                const answersGroupedByStep = [];
                Object.values(filteredQuestionsById)
                    .filter(q => q.isParent)
                    .forEach(question => {
                        answersGroupedByStep.push(
                            ...getAnswersOfQuestionTree(
                                question,
                                filteredQuestionsById,
                                claimApplication,
                                questionTypesById,
                                currencyOptions,
                            ),
                        );
                    });

                // sort the question - answer objects
                const sortedAnswersGroupedByStep = _.sortBy(
                    answersGroupedByStep,
                    function (item) {
                        return allQuestionIds.indexOf(item.question.code);
                    },
                );

                // group by step and exclude the attachments step
                return _.chain(sortedAnswersGroupedByStep)
                    .groupBy(s => s.question.questionGroupStepId)
                    .map((s, stepId) => {
                        const step = Object.values(questionGroupStepsById).find(
                            i => i.id === stepId,
                        );

                        return {
                            ...step,
                            questionsWithAnswers: s,
                        };
                    })
                    .filter(s => s.code !== codes.attachments)
                    .sortBy(s => s.rank)
                    .value();
            } catch (e) {
                return [];
            }
        },
    );

claimApplicationProjections.loadingClaimDetailsData = createSelector(
    state => state.claimApplication.loading,
    state => state.question.loading,
    state => state.questionType.loading,
    (loadingClaimApplication, loadingQuestion, loadingQuestionType) => {
        return (
            loadingClaimApplication || loadingQuestion || loadingQuestionType
        );
    },
);

claimApplicationProjections.loadingBankDetailsData = createSelector(
    state => state.question.loading,
    state => state.questionValidationRule.loading,
    state => state.questionType.loading,
    state => state.claimApplication.loadingBankAccountAnswers,
    (
        loadingQuestion,
        loadingQuestionValidationRule,
        loadingQuestionType,
        loadingBankAccountAnswers,
    ) => {
        return (
            loadingQuestion ||
            loadingQuestionValidationRule ||
            loadingQuestionType ||
            loadingBankAccountAnswers
        );
    },
);

claimApplicationProjections.initialBankDetailsFormValues = claimApplicationId =>
    createSelector(
        state =>
            state.claimApplication.claimApplication.byId[claimApplicationId],
        claimApplication => {
            if (!claimApplication) {
                return {};
            }
            let values = {};
            if (
                claimApplication &&
                claimApplication.claimBankAccount &&
                claimApplication.claimBankAccount.bankAccountTypeId ===
                    ids.bankAccountTypeUkOption
            ) {
                values = {
                    [claimBankDetailsQuestionCodes.bankAccountType]:
                        claimApplication.claimBankAccount.bankAccountTypeId,
                    [claimBankDetailsQuestionCodes.accoutnHolderNameUk]:
                        claimApplication.claimBankAccount.accountHolderName,
                    [claimBankDetailsQuestionCodes.accountNumber]:
                        claimApplication.claimBankAccount.accountNumber,
                    [claimBankDetailsQuestionCodes.sortCode]:
                        claimApplication.claimBankAccount.sortcode,
                };
            }
            if (
                claimApplication &&
                claimApplication.claimBankAccount &&
                claimApplication.claimBankAccount.bankAccountTypeId ===
                    ids.bankAccountTypeinternationalOption
            ) {
                values = {
                    [claimBankDetailsQuestionCodes.bankAccountType]:
                        claimApplication.claimBankAccount.bankAccountTypeId,
                    [claimBankDetailsQuestionCodes.accoutnHolderNameInternational]:
                        claimApplication.claimBankAccount.accountHolderName,
                    [claimBankDetailsQuestionCodes.iban]:
                        claimApplication.claimBankAccount.iban,
                };
            }

            return values;
        },
    );

claimApplicationProjections.answersOfSpecificByGroup = (
    claimApplicationId,
    groupId,
) =>
    createSelector(
        state =>
            state.claimApplication.claimApplication.byId[claimApplicationId],
        claimApplication => {
            if (!claimApplication || !claimApplication.answers) {
                return {};
            }
            return claimApplication.answers.filter(
                answer => answer.groupId === groupId,
            );
        },
    );

claimApplicationProjections.draftClaimApplicationList = () =>
    createSelector(
        state => state.answer.draftClaimApplicationList,
        draftClaimApplicationList => {
            if (!draftClaimApplicationList) {
                return null;
            }
            return draftClaimApplicationList.map(draft => ({
                ...draft,
                text: draft.claimReference
                    ? `Claim with Reference ${draft.claimReference} needs more information.`
                    : 'Not submitted Claim Application. Continue where you left off.',
            }));
        },
    );

claimApplicationProjections.claimApplicationAnswersGroupedByStepExtended = (
    claimApplicationId,
    currencyOptions,
    groupId = undefined,
) =>
    createSelector(
        state =>
            state.claimApplication.claimApplication.byId[claimApplicationId],
        state => state.question.question.byId,
        state => state.questionGroupStep.questionGroupStep.byId,
        state => state.questionType.questionType.byId,
        state => state.question.question.allIds,
        state => state.questionValidationRule.questionValidationRule,
        (
            claimApplication,
            questionsById,
            questionGroupStepsById,
            questionTypesById,
            allQuestionIds,
            questionValidationRules,
        ) => {
            if (
                !claimApplication ||
                Object.keys(questionsById).length === 0 ||
                Object.keys(questionGroupStepsById).length === 0 ||
                !currencyOptions
            ) {
                return {
                    answers: null,
                    groupStepWithQuestions: [],
                };
            }

            let filteredQuestionsById = GENERAL_FUNCTIONS.filterObjectBy(
                questionsById,
                q => q.groupId === groupId,
            );
            const answersGroupedByStep = [];
            Object.values(filteredQuestionsById)
                .filter(q => q.isParent)
                .forEach(question => {
                    answersGroupedByStep.push(
                        ...GENERAL_FUNCTIONS.getAnswersOfQuestionTree(
                            question,
                            filteredQuestionsById,
                            claimApplication,
                            questionTypesById,
                            currencyOptions,
                        ),
                    );
                });

            // construct the questions
            let questionsFormat = answersGroupedByStep
                .map(answer => {
                    return answer.question;
                })
                .sort((a, b) => {
                    return (
                        allQuestionIds.indexOf(a.code) -
                        allQuestionIds.indexOf(b.code)
                    );
                });
            // group by step and exclude the attachments step
            let qroupStepAndQuestions = questionsFormat.reduce(
                (acc, question) => {
                    const stepId = question?.questionGroupStepId;
                    const step = Object.values(questionGroupStepsById).find(
                        i => i.id === stepId,
                    );
                    if (!acc[stepId]) {
                        acc[stepId] = { ...step, questions: [] };
                    }

                    acc[stepId].questions.push(question);
                    return acc;
                },
                {},
            );

            let filterGroupStepAndQuestions = Object.values(
                qroupStepAndQuestions,
            )
                .filter(s => s.code !== codes.attachments)
                .sort((a, b) => a.rank - b.rank);

            let filterAnswers = claimApplication.answers
                ? claimApplication.answers
                      .map(answer => {
                          return GENERAL_FUNCTIONS.getAnswersForm(
                              filteredQuestionsById[answer.questionCode],
                              filteredQuestionsById,
                              claimApplication,
                              questionTypesById,
                              currencyOptions,
                          );
                      })
                      .reduce((acc, curr) => ({ ...acc, ...curr }), {})
                : null;

            return {
                answers:
                    filterAnswers && Object.values(filterAnswers).length > 0
                        ? filterAnswers
                        : {},
                groupStepWithQuestions: filterGroupStepAndQuestions,
                questionTypes: questionTypesById,
                questionValidationRules: questionValidationRules,
            };
        },
    );

export default claimApplicationProjections;
